import { useEffect, useState } from 'react';
import { Bell, Smartphone } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { enablePushNotifications, isPushSupported } from '../lib/push';
import type { NotificationSettings as NotificationSettingsType } from '../types';
import { useAuth } from '../contexts/AuthContext';

const settingLabels: Array<[keyof NotificationSettingsType, string]> = [
  ['notify_new_auction', 'Nuova asta / asta annullata'],
  ['notify_new_bid', 'Nuovo rilancio'],
  ['notify_chat', 'Nuovo messaggio in chat'],
  ['notify_30_minutes', 'Manca mezz’ora alla fine'],
  ['notify_5_minutes', 'Mancano 5 minuti alla fine'],
  ['notify_auction_closed', 'Asta chiusa e vincitore']
];

export function NotificationSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<NotificationSettingsType | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase.from('notification_settings').select('*').eq('user_id', user.id).single()
      .then(({ data, error }) => {
        if (error) console.error(error);
        setSettings(data as NotificationSettingsType | null);
      });
  }, [user]);

  const toggle = async (key: keyof NotificationSettingsType, value: boolean) => {
    if (!settings || !user) return;
    const next = { ...settings, [key]: value };
    setSettings(next);
    const { error } = await supabase.from('notification_settings').update({ [key]: value }).eq('user_id', user.id);
    if (error) {
      setMessage(error.message);
      setSettings(settings);
    }
  };

  const enablePush = async () => {
    if (!user) return;
    setBusy(true);
    setMessage(null);
    try {
      await enablePushNotifications(user.id);
      setMessage('Notifiche attivate su questo dispositivo.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Non riesco ad attivare le notifiche.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <div className="panel-header compact">
        <h2><Bell size={20} /> Notifiche</h2>
      </div>

      <button className="primary-button full" onClick={enablePush} disabled={busy || !isPushSupported()}>
        <Smartphone size={18} /> Attiva notifiche su questo dispositivo
      </button>
      {!isPushSupported() && <p className="muted">Questo browser non supporta Web Push.</p>}
      {message && <p className="muted">{message}</p>}

      <div className="settings-list">
        {settings && settingLabels.map(([key, label]) => (
          <label key={String(key)} className="setting-row">
            <span>{label}</span>
            <input
              type="checkbox"
              checked={Boolean(settings[key])}
              onChange={(event) => toggle(key, event.target.checked)}
            />
          </label>
        ))}
      </div>
    </section>
  );
}
